/**
 * ForecastAccuracyPage
 *
 * Phase 4 feedback loop: for each chatbot, compare what we PREDICTED
 * (from the profile-driven simulator) to what we ACTUALLY billed
 * (sum of llm_call_events in the same window). Drift is the signal that
 * a profile is stale or an agent's behavior is changing.
 *
 * Layout: one card per active chatbot.
 *   - Predicted p50 (from last snapshot)
 *   - Actual (from llm_call_events)
 *   - Variance % (green <10%, yellow <25%, red >25%)
 *   - Sample size + freshness
 */

import { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import {
  Alert,
  Card,
  Col,
  Empty,
  Row,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { Sidebar } from '@/components/layout/Sidebar';
import api from '@/services/api';
import { datePattern } from '@/utils/locale';

const { Title, Text } = Typography;

interface ChatbotAccuracyRow {
  chatbot_id: string;
  chatbot_name: string;
  tenant_name: string;
  sample_size: number;
  window_days: number;
  generated_at: string | null;
  predicted_p50_usd: number;
  actual_usd: number;
  variance_pct: number;
}

function variantForVariance(pct: number): 'success' | 'warning' | 'error' | 'default' {
  const abs = Math.abs(pct);
  if (abs < 10) return 'success';
  if (abs < 25) return 'warning';
  return 'error';
}

export function ForecastAccuracyPage() {
  const { t } = useLanguage();
  const { token } = theme.useToken();
  const [rows, setRows] = useState<ChatbotAccuracyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await api.get<{ success: boolean; rows: ChatbotAccuracyRow[] }>(
          '/cost-simulator/forecast-accuracy',
        );
        if (!cancelled) setRows(res.data.rows ?? []);
      } catch (err: any) {
        if (!cancelled) {
          // 404 means the endpoint hasn't been hit yet / no profile data —
          // surface a friendly message rather than a red error
          if (err?.response?.status === 404) {
            setRows([]);
          } else {
            setError(err?.message ?? t('costSimulator.failedToLoadAccuracy'));
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const summary = useMemo(() => {
    if (rows.length === 0) return null;
    const totalPredicted = rows.reduce((a, r) => a + r.predicted_p50_usd, 0);
    const totalActual = rows.reduce((a, r) => a + r.actual_usd, 0);
    const variance = totalActual > 0
      ? ((totalActual - totalPredicted) / totalActual) * 100
      : 0;
    const inBand = rows.filter(r => Math.abs(r.variance_pct) < 25).length;
    return {
      totalPredicted,
      totalActual,
      variance,
      inBand,
      total: rows.length,
    };
  }, [rows]);

  const columns: ColumnsType<ChatbotAccuracyRow> = [
    {
      title: t('insights.agentStats.agent'),
      dataIndex: 'chatbot_name',
      render: (n: string, r) => (
        <div>
          <div>{n}</div>
          <Text type="secondary" style={{ fontSize: 11 }} dir="auto">{r.tenant_name}</Text>
        </div>
      ),
    },
    {
      title: t('costSimulator.window'),
      dataIndex: 'window_days',
      align: 'right',
      render: (d: number) => `${d}d`,
    },
    {
      title: t('costSimulator.samples'),
      dataIndex: 'sample_size',
      align: 'right',
      render: (n: number) =>
        n < 30 ? <Tag color="orange">{n}</Tag> : <span>{n}</span>,
    },
    {
      title: t('costSimulator.predictedP50'),
      dataIndex: 'predicted_p50_usd',
      align: 'right',
      sorter: (a, b) => a.predicted_p50_usd - b.predicted_p50_usd,
      render: (n: number) => `$${n.toFixed(2)}`,
    },
    {
      title: t('costSimulator.actual'),
      dataIndex: 'actual_usd',
      align: 'right',
      sorter: (a, b) => a.actual_usd - b.actual_usd,
      defaultSortOrder: 'descend',
      render: (n: number) => <strong>${n.toFixed(2)}</strong>,
    },
    {
      title: t('costSimulator.variance'),
      dataIndex: 'variance_pct',
      align: 'right',
      sorter: (a, b) => Math.abs(a.variance_pct) - Math.abs(b.variance_pct),
      render: (p: number) => {
        const sign = p > 0 ? '+' : '';
        return (
          <Tag color={variantForVariance(p)}>
            {sign}{p.toFixed(1)}%
          </Tag>
        );
      },
    },
    {
      title: t('costSimulator.profileGenerated'),
      dataIndex: 'generated_at',
      render: (g: string | null) => {
        if (!g) return '—';
        const d = dayjs(g);
        return d.isValid() ? d.format(datePattern('monthDayTime')) : g;
      },
    },
  ];

  return (
    <div style={{ display: 'flex', height: '100vh' }}>
      <Sidebar />
      <main style={{ flex: 1, padding: 24, overflow: 'auto' }}>
        <Title level={3}>{t('costSimulator.forecastAccuracy.title')}</Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 24 }}>
          {t('costSimulator.forecastAccuracy.introPrefix')}{' '}
          <code>llm_call_events</code>{t('costSimulator.forecastAccuracy.introSuffix')}
        </Text>

        {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

        {summary && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title={t('costSimulator.forecastAccuracy.agentsInBand')}
                  value={summary.inBand}
                  suffix={` / ${summary.total}`}
                  valueStyle={{
                    color: summary.inBand === summary.total ? '#10b981' : '#f59e0b',
                  }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  &lt;25% variance from forecast
                </Text>
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title={t('costSimulator.forecastAccuracy.predictedTotal')}
                  value={summary.totalPredicted}
                  precision={2}
                  prefix="$"
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title={t('costSimulator.forecastAccuracy.actualTotal')}
                  value={summary.totalActual}
                  precision={2}
                  prefix="$"
                  valueStyle={{ color: token.colorPrimary }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={6}>
              <Card>
                <Statistic
                  title={t('costSimulator.forecastAccuracy.overallVariance')}
                  value={summary.variance}
                  precision={1}
                  suffix="%"
                  valueStyle={{
                    color:
                      Math.abs(summary.variance) < 10
                        ? '#10b981'
                        : Math.abs(summary.variance) < 25
                          ? '#f59e0b'
                          : '#ef4444',
                  }}
                />
              </Card>
            </Col>
          </Row>
        )}

        <Card>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48 }}>
              <Spin size="large" />
            </div>
          ) : rows.length === 0 ? (
            <Empty
              description={
                <span>{t('costSimulator.forecastAccuracy.noData')}</span>
              }
            />
          ) : (
            <Table<ChatbotAccuracyRow>
              rowKey="chatbot_id"
              dataSource={rows}
              columns={columns}
              pagination={{ pageSize: 25 }}
              size="small"
            />
          )}
        </Card>
      </main>
    </div>
  );
}

export default ForecastAccuracyPage;
